import _ from 'underscore';

import {
  TaskData,
  WorksheetTaskLoadingStateMapping,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';

const WORKSHEET_TASK_STATUSES_TO_SHOW_ACTION_DROPDOWN = [
  WorksheetTaskStatusType.PENDING,
  WorksheetTaskStatusType.IN_PROGRESS,
];

const LOADING_WORKSHEET_STATUSES: ReadonlyArray<WorksheetTaskStatusType> = Object.values(
  WorksheetTaskLoadingStateMapping
).filter((status): status is WorksheetTaskStatusType => status != null);

// A planned run is waiting on its previous unit operation, and a not ready worksheet's method tasks disagree on
// their status, so neither can be transitioned from the worksheet. Ready is not a method task status.
const NON_ACTIONABLE_WORKSHEET_STATUSES: ReadonlyArray<WorksheetTaskStatusType> = [
  WorksheetTaskStatusType.NOT_READY,
  WorksheetTaskStatusType.READY,
  WorksheetTaskStatusType.PLANNED,
  ...LOADING_WORKSHEET_STATUSES,
];

/** Whether a worksheet with this status has not been started yet, so its steps stay locked. */
export function isWorksheetStatusPending(status: WorksheetTaskStatusType | undefined): boolean {
  return (
    status == null || status === WorksheetTaskStatusType.PENDING || status === WorksheetTaskStatusType.PLANNED
  );
}

/** Whether the worksheet status pill can be used to transition a worksheet with this status. */
export function isWorksheetStatusActionable(status: WorksheetTaskStatusType): boolean {
  return !NON_ACTIONABLE_WORKSHEET_STATUSES.includes(status);
}

export const BUTTON_DISABLED_PREV_RUN_NOT_COMPLETE =
  'Button is disabled because the previous unit operation has not been completed.';

export const BUTTON_DISABLED_ALL_TASKS_NOT_CREATED =
  'Button is disabled because this worksheet is still being compiled.';

export interface WorksheetControlVariables {
  isWorksheetPending: boolean;
  worksheetLastActionText: string | null;
  isWorksheetStatusBadgeDisabled: boolean;
  showWorksheetStatusDropdownButton: boolean;
  worksheetStatusBadgeTooltipText: string | null;
}

interface WorksheetControlOptions {
  /** Whether the worksheet still has step documents that have not been saved */
  hasUnsavedStepDocuments: boolean;
  /** Whether all PMEIs have their method tasks linked */
  areAllMethodTasksCreated: boolean;
  /**
   * Whether all previous PMEIs' tasks have reached terminal status
   * (used in delayed mapping / split pool where current PMEIs may not have tasks yet)
   */
  previousPMEITasksCompleted?: boolean;
  /** Whether delayed mapping (split pool) is enabled */
  isDelayedMappingEnabled?: boolean;
}

/**
 * Function to output booleans that control the UI behavior of WorksheetPageHeader & other worksheet-level components
 * @param worksheetTaskData task data of worksheet output by function WorksheetUtils.extractTaskData
 * @param options see interface WorksheetControlOptions
 * @returns see interface WorksheetControlVariables
 */
export const getWorksheetControlVariables = (
  worksheetTaskData: TaskData,
  options: WorksheetControlOptions
): WorksheetControlVariables => {
  const {
    hasUnsavedStepDocuments,
    areAllMethodTasksCreated,
    previousPMEITasksCompleted = true,
    isDelayedMappingEnabled = false,
  } = options;
  const hasTaskData = !_.isEmpty(worksheetTaskData);
  // In the standard (non-delayed) case, prevMethodComplete is true when task data exists
  // (i.e. tasks have been created for this worksheet's PMEIs).
  // In delayed mapping, PMEIs have status PENDING but no task IDs — use previous PMEI
  // task completion to determine if this worksheet is ready to start.
  const hasNoLinkedTasks = isDelayedMappingEnabled && hasTaskData && !worksheetTaskData.taskIds?.length;
  const prevMethodComplete = hasNoLinkedTasks ? previousPMEITasksCompleted : hasTaskData;
  const tasksNotReady = hasNoLinkedTasks ? !previousPMEITasksCompleted : !areAllMethodTasksCreated;
  const {status} = worksheetTaskData;
  const noStatusAndNotReady = !status && tasksNotReady;

  const isWorksheetPending = isWorksheetStatusPending(status) || tasksNotReady;

  // assemble text on page header describing worksheet's last action
  const worksheetLastActionVerb =
    status == null || isWorksheetStatusPending(status)
      ? null
      : status === WorksheetTaskStatusType.IN_PROGRESS
        ? 'started'
        : status.toLowerCase();
  const worksheetLastActionText =
    worksheetLastActionVerb && status
      ? LOADING_WORKSHEET_STATUSES.includes(status)
        ? `Run ${worksheetLastActionVerb}...`
        : `Run ${worksheetLastActionVerb} on ${worksheetTaskData.timestamp}`
      : null;

  // determine if WorksheetStatusBadge should be disabled
  const isWorksheetStatusBadgeDisabled =
    hasUnsavedStepDocuments ||
    noStatusAndNotReady ||
    (status != null && !isWorksheetStatusActionable(status)) ||
    (status === WorksheetTaskStatusType.IN_PROGRESS && !areAllMethodTasksCreated) ||
    !prevMethodComplete ||
    tasksNotReady;

  // determine if the dropdown button for WorksheetStatusBadge should be shown
  const showWorksheetStatusDropdownButton = Boolean(
    !status || !prevMethodComplete || WORKSHEET_TASK_STATUSES_TO_SHOW_ACTION_DROPDOWN.includes(status)
  );
  // when worksheetStatusBadgeTooltipText is null we disable tooltip from showing
  const worksheetStatusBadgeTooltipText =
    !prevMethodComplete || status === WorksheetTaskStatusType.PLANNED
      ? BUTTON_DISABLED_PREV_RUN_NOT_COMPLETE
      : tasksNotReady
        ? BUTTON_DISABLED_ALL_TASKS_NOT_CREATED
        : null;

  return {
    isWorksheetPending,
    worksheetLastActionText,
    isWorksheetStatusBadgeDisabled,
    showWorksheetStatusDropdownButton,
    worksheetStatusBadgeTooltipText,
  };
};
